import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import {
  Wrench, AlertCircle, ShieldAlert, ShieldOff, CalendarClock, TrendingUp,
  Circle, PlayCircle, CheckCircle2, Trash2, ChevronRight, X, Sparkles, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { getCategoryColor } from '../utils/helpers';
import TicketThreadDrawer from '../components/TicketThreadDrawer';

const ISSUE_META = {
  underinsured:  { label: 'Underinsured',     icon: ShieldAlert,  tone: 'red' },
  uninsured:     { label: 'No cover',         icon: ShieldOff,    tone: 'amber' },
  expiring:      { label: 'Renewal due',      icon: CalendarClock,tone: 'amber' },
  concentration: { label: 'Concentration',    icon: TrendingUp,   tone: 'amber' },
  valuation:     { label: 'Revaluation due',  icon: TrendingUp,   tone: 'gold' },
  other:         { label: 'Other',            icon: AlertCircle,  tone: 'gold' },
};

const STATUS_META = {
  open:         { label: 'Open',         icon: Circle,        tone: 'gold' },
  in_progress:  { label: 'In progress',  icon: PlayCircle,    tone: 'amber' },
  resolved:     { label: 'Resolved',     icon: CheckCircle2,  tone: 'emerald' },
  dismissed:    { label: 'Dismissed',    icon: X,             tone: 'muted' },
};

const TONE_PILL = {
  gold:    'bg-gold/15 text-gold border-gold/25',
  red:     'bg-red-500/15 text-red-400 border-red-500/25',
  amber:   'bg-amber-500/15 text-amber-400 border-amber-500/25',
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  muted:   'bg-zinc-500/15 text-zinc-400 border-zinc-500/25',
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const TicketRow = ({ t, onAdvance, onResolve, onDismiss, onDelete, onOpenThread }) => {
  const im = ISSUE_META[t.issue] || ISSUE_META.other;
  const sm = STATUS_META[t.status] || STATUS_META.open;
  const Icon = im.icon;
  const SIcon = sm.icon;
  const hasAdvisor = !!t.assigned_partner_id;
  const noteCount = (t.notes?.length) || 0;
  return (
    <div className="luxury-card p-4 flex items-start gap-4 hover:border-gold/30 transition-colors group" data-testid={`ticket-${t.id}`}>
      <div className={`w-10 h-10 rounded flex items-center justify-center border ${TONE_PILL[im.tone]} flex-shrink-0`}>
        <Icon size={16} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="text-white text-sm font-medium truncate">{t.title}</p>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TONE_PILL[im.tone]} uppercase tracking-wider`}>{im.label}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TONE_PILL[sm.tone]} uppercase tracking-wider inline-flex items-center gap-1`}>
            <SIcon size={9} /> {sm.label}
          </span>
          {hasAdvisor && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-gold/30 bg-gold/5 text-gold uppercase tracking-wider">
              With advisor
            </span>
          )}
        </div>
        {t.asset_name && (
          <p className="text-[11px] text-muted-foreground">
            <Link to={`/assets/${t.asset_id}`} className={`${getCategoryColor(t.asset_category)} hover:underline`}>{t.asset_name}</Link>
            <span className="mx-2 text-zinc-600">·</span>
            <span>Opened {formatDate(t.created_at)}</span>
            {t.resolved_at && <><span className="mx-2 text-zinc-600">·</span><span className="text-emerald-400">Resolved {formatDate(t.resolved_at)}</span></>}
          </p>
        )}
        {t.detail && (
          <p className="text-[11px] text-muted-foreground/80 mt-2 leading-relaxed line-clamp-2">{t.detail}</p>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {hasAdvisor && (
          <button
            onClick={() => onOpenThread?.(t)}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gold hover:text-amber-300 px-2 py-1 rounded border border-gold/30 hover:border-gold/60"
            data-testid={`ticket-thread-${t.id}`}
          >
            <MessageSquare size={11} /> Thread
            {noteCount > 0 && <span className="ml-1 text-[10px] bg-gold/15 text-gold px-1.5 rounded-full">{noteCount}</span>}
          </button>
        )}
        {t.status === 'open' && (
          <Button size="sm" variant="ghost" onClick={() => onAdvance(t)} className="text-amber-400 hover:text-amber-300 h-7 text-[10px] uppercase tracking-wider" data-testid={`ticket-start-${t.id}`}>
            Start
          </Button>
        )}
        {t.status === 'in_progress' && (
          <Button size="sm" variant="ghost" onClick={() => onResolve(t)} className="text-emerald-400 hover:text-emerald-300 h-7 text-[10px] uppercase tracking-wider" data-testid={`ticket-resolve-${t.id}`}>
            Resolve
          </Button>
        )}
        {(t.status === 'open' || t.status === 'in_progress') && (
          <Button size="sm" variant="ghost" onClick={() => onDismiss(t)} className="text-muted-foreground hover:text-white h-7 text-[10px] uppercase tracking-wider" data-testid={`ticket-dismiss-${t.id}`}>
            Dismiss
          </Button>
        )}
        {(t.status === 'resolved' || t.status === 'dismissed') && (
          <button onClick={() => onDelete(t)} className="text-muted-foreground hover:text-red-400 p-1" data-testid={`ticket-delete-${t.id}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default function ActionTickets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [threadTicket, setThreadTicket] = useState(null);

  const fetchData = async () => {
    try {
      const r = await api.listTickets();
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.tickets;
    if (filter === 'active') return data.tickets.filter(t => t.status === 'open' || t.status === 'in_progress');
    return data.tickets.filter(t => t.status === filter);
  }, [data, filter]);

  const update = async (t, patch) => {
    try {
      await api.updateTicket(t.id, patch);
      toast.success(patch.status === 'resolved' ? 'Marked as resolved' : 'Updated');
      fetchData();
    } catch {
      toast.error('Could not update');
    }
  };

  const onAdvance = (t) => update(t, { status: 'in_progress' });
  const onResolve = (t) => update(t, { status: 'resolved' });
  const onDismiss = (t) => update(t, { status: 'dismissed' });
  const onDelete = async (t) => {
    if (!window.confirm('Delete this ticket permanently?')) return;
    try { await api.deleteTicket(t.id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Could not delete'); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="tickets-loading">
          <Skeleton className="h-3 w-24 mb-3 bg-gold/10" />
          <Skeleton className="h-9 w-72 mb-8 bg-white/5" />
          {[0,1,2].map(i => <Skeleton key={i} className="h-20 bg-white/5 mb-3" />)}
        </div>
      </DashboardLayout>
    );
  }

  const c = data?.counts || { open:0, in_progress:0, resolved:0, dismissed:0, total:0 };
  const FILTERS = [
    { id: 'open',        label: 'Open',         count: c.open },
    { id: 'in_progress', label: 'In progress',  count: c.in_progress },
    { id: 'active',      label: 'Both',         count: c.open + c.in_progress },
    { id: 'resolved',    label: 'Resolved',     count: c.resolved },
    { id: 'dismissed',   label: 'Dismissed',    count: c.dismissed },
    { id: 'all',         label: 'All',          count: c.total },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="tickets-page">
        <div className="mb-8">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Action queue</p>
          <h1 className="font-serif text-3xl text-white">Your wealth, your todo list</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Every "Fix this" you save from the Risk page lives here until you have actioned it. Open, work in progress, resolved - all in one queue.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tickets-stats">
          {[
            { label: 'Open',        value: c.open,        tone: 'gold' },
            { label: 'In progress', value: c.in_progress, tone: 'amber' },
            { label: 'Resolved',    value: c.resolved,    tone: 'emerald' },
            { label: 'Total',       value: c.total,       tone: 'muted' },
          ].map(s => (
            <div key={s.label} className={`luxury-card p-4 ${s.tone === 'gold' ? 'border-gold/30' : s.tone === 'amber' ? 'border-amber-500/30' : s.tone === 'emerald' ? 'border-emerald-500/30' : ''}`}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
              <p className={`font-serif text-2xl ${s.tone === 'gold' ? 'text-gold' : s.tone === 'amber' ? 'text-amber-400' : s.tone === 'emerald' ? 'text-emerald-400' : 'text-white'}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="tickets-filters">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`tickets-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded border transition-colors ${
                filter === f.id
                  ? 'bg-gold/15 text-gold border-gold/40'
                  : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20 hover:text-white'
              }`}
            >
              {f.label} <span className="opacity-60 ml-1">{f.count}</span>
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="luxury-card p-10 text-center" data-testid="tickets-empty">
            <Wrench size={28} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-serif text-xl text-white mb-2">
              {data.counts.total === 0 ? 'No action tickets yet' : 'No tickets match this filter'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              {data.counts.total === 0
                ? 'Visit the Risk page and click "Fix this" on any flagged asset. Save it to your queue and track it through to resolution.'
                : 'Try a different filter above.'}
            </p>
            {data.counts.total === 0 && (
              <Link to="/risk">
                <Button className="btn-primary" data-testid="tickets-go-to-risk">Open Risk page</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {visible.map(t => (
              <TicketRow key={t.id} t={t} onAdvance={onAdvance} onResolve={onResolve} onDismiss={onDismiss} onDelete={onDelete} onOpenThread={(t) => setThreadTicket(t)} />
            ))}
          </div>
        )}

        <div className="mt-8 luxury-card p-4 flex items-center gap-3 border-gold/20" data-testid="tickets-hint">
          <Sparkles size={16} className="text-gold flex-shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            When you click "Fix this" on the Risk page you can either draft an email immediately or save the
            task here to act on later. Tickets you resolve stay in the history for an audit trail.
          </p>
          <Link to="/risk">
            <span className="inline-flex items-center gap-1 text-xs text-gold hover:underline whitespace-nowrap">
              Open Risk page <ChevronRight size={11} />
            </span>
          </Link>
        </div>
      </div>

      <TicketThreadDrawer
        ticketId={threadTicket?.id}
        clientName={threadTicket?.title}
        open={!!threadTicket}
        onClose={() => { setThreadTicket(null); fetchData(); }}
        viewerRole="owner"
      />
    </DashboardLayout>
  );
}
